import { useState, useEffect } from "react";
import { Folder, File, ChevronRight, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Heading from "../../ui/Heading";
import InfoTooltip from "@/ui/InfoTooltip";
import { removeUserUploadedDocument } from "../../services/apiUser"; // Assuming this is the delete function
import { useOptimisticRemove } from "../../hooks/useOptimisticRemove"; // Custom hook for optimistic removal
import { HiTrash } from "react-icons/hi2";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import { useGetUploadDocuments } from "@/features/business_profile/useGetUploadDocuments";
import { Skeleton } from "@/components/ui/skeleton";

export default function DocumentFolderView() {
  const { data, isLoading, isSuccess } = useGetUploadDocuments();
  const [expandedFolders, setExpandedFolders] = useState([]);
  const [documents, setDocuments] = useState({});

  const { mutate, isError, error } = useOptimisticRemove(
    ["uploadedDocuments"],
    removeUserUploadedDocument,
    (tempDoc, documentToDelete) => tempDoc.id !== documentToDelete.id
  );

  useEffect(() => {
    if (isSuccess) {
      const filteredData = data.filter((d) => d.type !== "Business Profile");

      const groupedDocuments = filteredData.reduce((acc, doc) => {
        const folderName = doc.type || "Other";
        if (!acc[folderName]) {
          acc[folderName] = [];
        }
        acc[folderName].push(doc);
        return acc;
      }, {});

      setDocuments(groupedDocuments);
    }
  }, [data, isSuccess]);

  const toggleFolder = (folderName) => {
    setExpandedFolders((prev) =>
      prev.includes(folderName)
        ? prev.filter((name) => name !== folderName)
        : [...prev, folderName]
    );
  };

  const handleDelete = async (folderName, id) => {
    const docToDelete = documents[folderName].find((doc) => doc.id === id);

    setDocuments((prev) => ({
      ...prev,
      [folderName]: prev[folderName].filter((doc) => doc.id !== id),
    }));

    try {
      await mutate(docToDelete.title);
    } catch (err) {
      setDocuments((prev) => ({
        ...prev,
        [folderName]: [...prev[folderName], docToDelete],
      }));
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="w-full h-24" />
        <Skeleton className="w-full h-24" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2 mb-2">
        <Heading as="h2" className="font-bold" id="cp-profile-doc-upload">
          Indexed Documents
        </Heading>
        <InfoTooltip description="Review and manage your uploaded documents below." />
      </div>

      {Object.entries(documents).map(([folderName, docs]) => (
        <div key={folderName} className="border rounded-lg p-2">
          <Button
            variant="ghost"
            className="w-full justify-start"
            onClick={() => toggleFolder(folderName)}
          >
            {expandedFolders.includes(folderName) ? (
              <ChevronDown className="mr-2" />
            ) : (
              <ChevronRight className="mr-2" />
            )}
            <Folder className="mr-2" />
            {folderName} ({docs.length})
          </Button>
          {expandedFolders.includes(folderName) && (
            <div className="ml-6 mt-2 space-y-2">
              {docs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <File className="mr-2" />
                    <span>{doc.title}</span>
                  </div>
                  <div className="flex items-center">
                    <Badge
                      variant={
                        doc.status === "Done"
                          ? "success"
                          : doc.status === "Processing"
                          ? "processing"
                          : "destructive"
                      }
                    >
                      {doc.status}
                    </Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0 ml-2">
                          <span className="sr-only">More options</span>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem
                          onClick={() => handleDelete(folderName, doc.id)}
                          className="text-red-500"
                        >
                          <HiTrash className="mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
